var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import cn from 'classnames';
import mergeRefs from 'merge-refs';
import { forwardRef, useCallback, useMemo, useRef, useState, } from 'react';
import { Droplist } from '@snack-uikit/list';
import { SearchPrivate } from '@snack-uikit/search-private';
import { PRIVATE_SEARCH_TEST_IDS, SIZE, TEST_IDS } from '../../constants';
import { SearchDecorator } from '../SearchDecorator';
import styles from './styles.module.css';
export const SearchAutocomplete = forwardRef(function SearchAutocomplete(_a, ref) {
    var { size = SIZE.S, value, onChange, placeholder, options = [], loading, outline, onSubmit, onFocus, className } = _a, rest = __rest(_a, ["size", "value", "onChange", "placeholder", "options", "loading", "outline", "onSubmit", "onFocus", "className"]);
    const scrollRef = useRef(null);
    const localRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const handleOptionKeyDown = useCallback((e) => {
        var _a, _b;
        if (e.key.length === 1) {
            e.stopPropagation();
            (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            (_b = scrollRef.current) === null || _b === void 0 ? void 0 : _b.scroll(0, 0);
            return;
        }
        // ignoring special keys (tab, arrows, backspace, etc.)
        if (!['ArrowUp', 'ArrowDown'].includes(e.key)) {
            e.stopPropagation();
        }
        else {
            e.preventDefault();
        }
    }, [scrollRef]);
    const handleKeyDown = (cb) => (e) => {
        if (e.key === ' ') {
            e.stopPropagation();
        }
        if (e.key.length === 1) {
            setIsOpen(true);
        }
        cb === null || cb === void 0 ? void 0 : cb(e);
    };
    const items = useMemo(() => options.map((_a, idx) => {
        var _b;
        var { onClick } = _a, item = __rest(_a, ["onClick"]);
        return (Object.assign(Object.assign({}, item), { onClick: (e) => {
                var _a;
                // Dirty hack: by default list items call onClick by 'Space' || 'Enter'
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                if (!(e.type === 'keydown' && (e === null || e === void 0 ? void 0 : e.key) === ' ')) {
                    onClick === null || onClick === void 0 ? void 0 : onClick(e);
                    if (item.id) {
                        onChange === null || onChange === void 0 ? void 0 : onChange(String(item.id));
                        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(String(item.id));
                    }
                    else if (typeof item.content === 'string') {
                        onChange === null || onChange === void 0 ? void 0 : onChange(item.content);
                        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(item.content);
                    }
                    setIsOpen(false);
                    (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }
            }, onKeyDown: handleOptionKeyDown, 'data-test-id': `${TEST_IDS.option}__${(_b = item.id) !== null && _b !== void 0 ? _b : idx}` }));
    }), [handleOptionKeyDown, onChange, onSubmit, options]);
    return (_jsx("div", Object.assign({ className: cn(styles.wrap, className) }, rest, { children: _jsx(Droplist, { open: isOpen && options.length > 0, scroll: true, size: size, onOpenChange: setIsOpen, "data-test-id": TEST_IDS.droplist, triggerClassName: styles.triggerClassName, scrollRef: scrollRef, triggerElemRef: localRef, items: items, loading: loading, children: ({ onKeyDown }) => {
                var _a;
                return (_jsx(SearchDecorator, { size: size, outline: outline || undefined, focused: (isOpen && Boolean((_a = localRef.current) === null || _a === void 0 ? void 0 : _a.value)) || undefined, "data-test-id": TEST_IDS.decorator, children: _jsx(SearchPrivate, { loading: loading, value: value, onChange: onChange, onSubmit: onSubmit, placeholder: placeholder, ref: mergeRefs(ref, localRef), onKeyDown: handleKeyDown(onKeyDown), onFocus: onFocus, size: size, "data-test-id": PRIVATE_SEARCH_TEST_IDS.field }) }));
            } }) })));
});
